import React, { useMemo, useState } from "react";
import { DropdownCondition } from "../../queries/getCrmRequests";
import { GetLookUpOptionsParamsType, LookUpOptionsType } from "../../queries/getCrmLookUpOptions";
import { ObjectType } from "../../queries/getCrmListFieldDefinitions";
import { useDebounce } from "../../../hooks/useDebounce";
import { unionBy } from "lodash";
import { DropdownFilter } from "./DropdownFilter";
import { nestedErrorMessageSelector, IBaseControlProps } from "./Utils";
import { Label as SemanticLabel } from "semantic-ui-react";
import { Controller } from "react-hook-form";

interface ILookUpFilterProps {
  label: string;
  defaultValues?: (string | number)[];
  defaultOperator?: DropdownCondition;
  className?: string;
  useEmptyOperator?: boolean;
  onChange?: (values: (string | number)[]) => void;
  onOperatorChange?: (operator: DropdownCondition, values: (string | number)[]) => void;
  lookUpType: ObjectType;
  lookUpField: string;
  lookUpNeedsSearch: boolean;
  lookUpOptionsFunction: (
    params: GetLookUpOptionsParamsType,
    enabled: boolean,
  ) => {
    isLoading: boolean;
    data:
      | {
          options: LookUpOptionsType[];
        }
      | undefined;
  };
}

export const LookUpFilter = ({
  label,
  className,
  defaultOperator = "Any",
  defaultValues = [],
  lookUpField,
  lookUpType,
  lookUpNeedsSearch,
  lookUpOptionsFunction,
  onChange,
  onOperatorChange,
  useEmptyOperator = true,
}: ILookUpFilterProps) => {
  const [operator, setOperator] = useState<DropdownCondition>(defaultOperator);
  //either pre load in all options or load in options based on what default look up values are present
  const shouldLoadDefaultLookUpOptions = !lookUpNeedsSearch || defaultValues.length > 0;
  const { isLoading: defaultLookUpOptionsLoading, data: defaultLookUpOptionsResponse } = lookUpOptionsFunction(
    {
      lookUpType,
      lookUpField,
      ids: lookUpNeedsSearch && defaultValues.length > 0 ? defaultValues.map(String).join(",") : undefined, //only search for default values, not any the user adds after component mounts
    },
    shouldLoadDefaultLookUpOptions,
  );
  const [lookUpSearch, setLookUpSearch] = useState("");
  const debouncedLookUpSearch = useDebounce(lookUpSearch, 500);
  //update look up options based on search if look up field needs search
  const { isLoading: lookUpOptionsLoading, data: lookUpOptionsResponse } = lookUpOptionsFunction(
    {
      lookUpType,
      lookUpField,
      search: debouncedLookUpSearch,
    },
    debouncedLookUpSearch.length > 1 && lookUpNeedsSearch,
  );

  const convertLookUpOptionResponseToDropdownOptions = (options?: LookUpOptionsType[]) => {
    if (!options) return [];
    return options.map((option) => ({
      key: String(option.id),
      text: option.label,
      value: String(option.id),
    }));
  };

  // save any look up options that are selected by the user so we don't lose them when the search changes
  const [selectedOptions, setSelectedOptions] = useState<
    {
      key: string;
      text: string;
      value: string;
    }[]
  >([]);

  const lookUpOptions = useMemo(() => {
    return unionBy(
      selectedOptions,
      unionBy(
        convertLookUpOptionResponseToDropdownOptions(defaultLookUpOptionsResponse?.options),
        convertLookUpOptionResponseToDropdownOptions(lookUpOptionsResponse?.options),
        "value",
      ),
      "value",
    );
  }, [defaultLookUpOptionsResponse, lookUpOptionsResponse, selectedOptions]);

  const isLoading = defaultLookUpOptionsLoading || lookUpOptionsLoading;

  const onSearchChange = (searchQuery: string) => setLookUpSearch(searchQuery);

  const isCurrentOperatorIsDropdown = operator === "Any" || operator === "NotAny";

  return (
    <DropdownFilter
      label={label}
      className={className}
      options={lookUpOptions}
      defaultValues={defaultValues}
      defaultOperator={defaultOperator}
      useEmptyOperator={useEmptyOperator}
      onChange={(values) => {
        if (isCurrentOperatorIsDropdown) {
          const newOptions = values.map((value) => {
            const existingOption = lookUpOptions.find((option) => option.value === value);
            if (existingOption) return existingOption;
            return {
              key: String(value),
              text: String(value),
              value: String(value),
            };
          });
          setSelectedOptions(newOptions);
          onChange?.(values);
          return;
        }
        const stringValues = values.map(String);
        onChange?.(stringValues);
      }}
      onOperatorChange={(operator, values) => {
        setOperator(operator);
        if (operator === "Empty" || operator === "NotEmpty") {
          onOperatorChange?.(operator, []);
          return;
        }
        const isNewOperatorDropdown = operator === "Any" || operator === "NotAny";
        if (isNewOperatorDropdown) {
          onOperatorChange?.(operator, values);
          return;
        }
        const stringValues = values.map(String);
        onOperatorChange?.(operator, stringValues);
      }}
      onSearchChange={onSearchChange}
      isLoading={isLoading}
      stringFieldWhenNoOptions={false}
    />
  );
};

interface IControlledLookUpFilterProps extends IBaseControlProps, ILookUpFilterProps {}

export const ControlledLookUpFilter = ({ control, name, errors, ...props }: IControlledLookUpFilterProps) => {
  const errorMessage = nestedErrorMessageSelector(errors)(name);
  return (
    <Controller
      control={control}
      name={name}
      errors={errors}
      defaultValue={{
        jsonValue: props.defaultValues || [],
        dropdownCondition: props.defaultOperator || "Any",
      }}
      render={({ onChange, value: formValue }) => (
        <>
          <LookUpFilter
            {...props}
            defaultValues={formValue?.jsonValue || props.defaultValues}
            defaultOperator={formValue?.dropdownCondition || props.defaultOperator}
            onChange={(values) => {
              onChange({
                ...formValue,
                jsonValue: values,
              });
              if (props.onChange) {
                props.onChange(values);
              }
            }}
            onOperatorChange={(operator, values) => {
              onChange({
                ...formValue,
                dropdownCondition: operator,
                jsonValue: values,
              });
              if (props.onOperatorChange) {
                props.onOperatorChange(operator, values);
              }
            }}
          />
          {!!errorMessage && (
            <SemanticLabel className='tw-ml-0' basic prompt pointing='above'>
              {errorMessage}
            </SemanticLabel>
          )}
        </>
      )}
    />
  );
};
